import {
  Avatar,
  Box,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  styled,
  Toolbar,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { Icon } from 'components';
import blinkLinkIcon from 'assets/images/app-icon.svg';
import logoWhite from 'assets/images/logo-name-white.svg';
import { analyticsAppRoutes, appRoutes } from 'routes';
import { isSmallScreen, localRedirect } from 'utils';

import { SIDEBAR_WIDTH } from 'config';
import { IconMenuItem } from 'mui-nested-menu';
import { useSelector } from 'react-redux';
import { makeSelectUserData } from 'pages/AppManagement/selectors';
// import { useElementSize } from 'pages/AppManagement/hooks/UseElementSize';
import { Menu } from '../../../../../components/Menu/Menu';
import { NestedMenuItem } from '../../../../../components/Menu/NestedMenuItem';
import { SidebarProps } from './types';
import styles from './container.module.scss';
import { iconMapKeys } from '.icons';

const height = 65;

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: isSmallScreen() ? 'absolute' : 'relative',
    whiteSpace: 'nowrap',
    width: SIDEBAR_WIDTH,
    background: 'transparent',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    boxSizing: 'border-box',
    overflow: 'visible',

    ...(!open && {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(7),
      overflow: 'visible'
    })
  }
}));

const StyledAccount = styled('div', {
  shouldForwardProp: prop => prop !== 'open'
})<{ open: boolean }>(({ theme, open }) => ({
  display: 'flex',
  gap: '.5rem',
  flexDirection: 'column',
  border: ' 1px solid rgba(255, 255, 255, 0.10)',
  padding: open ? theme.spacing(1, 1.5) : theme.spacing(1, 1),
  borderRadius: Number(theme.shape.borderRadius) * 3,
  backgroundColor: 'rgba(91, 78, 221, 0.10)'
}));

const CustomList = styled(List)<{ component?: React.ElementType }>(({ theme }) => ({
  '& .MuiListItemButton-root': {
    paddingLeft: 12,
    paddingRight: 12,
    color: 'white',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    marginLeft: '5px !important',

    '& .MuiListItemIcon-root': {
      transition: 'transform 0.3s ease',
      minWidth: 40
    },

    '& .MuiListItemText-root': {
      opacity: 1,
      transition: 'opacity 0.3s ease',
      whiteSpace: 'nowrap'
    },

    '&.active': {
      borderLeft: `10px solid #140430`,
      marginLeft: '0 !important',
      '& .MuiListItemText-primary': {
        color: 'white',
        fontWeight: 600
      },

      '& svg': {
        g: {
          opacity: 1
        },
        path: {
          fill: 'white'
        }
      }
    },

    '&:hover': {
      background: 'linear-gradient(to right, #140430, #360A82)'
    }
  }
}));

const CollapsedList = styled(CustomList)<{ open: boolean }>(({ open, theme }) => ({
  overflow: 'visible',

  '& .MuiListItemButton-root': {
    overflow: 'visible',
    margin: '8px',
    borderRadius: open ? '12px' : '8px',
    transition: 'all 0.3s ease',
    width: open ? 'auto' : '40px',
    height: open ? 'auto' : '40px',

    '&.active': {
      background: 'linear-gradient(to right, #140430, #360A82)',
      position: 'relative',

      '&::before': {
        content: '""',
        position: 'absolute',
        top: -1,
        left: -1,
        right: -1,
        bottom: -1,
        borderRadius: 'inherit',
        padding: '1px',
        background: 'linear-gradient(90deg, #2E9EE4, #833CE2)',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none'
      },

      '& .MuiListItemText-primary': {
        color: 'white',
        fontWeight: 600
      },

      '& svg': {
        g: {
          opacity: 1
        },
        path: {
          fill: 'white'
        }
      }
    },

    ...(!open && {
      width: '40px',
      height: '40px',
      minWidth: '40px',
      minHeight: '40px',
      maxWidth: '40px',
      maxHeight: '40px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '& .MuiListItemIcon-root': {
        minWidth: 'unset',
        width: '24px',
        height: '24px',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translate(3px, 0px)'
      },

      '&.active': {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -1,
          left: -1,
          right: -1,
          bottom: -1,
          width: '42px',
          height: '42px',
          borderRadius: '8px',
          padding: '1px',
          background: 'linear-gradient(90deg, #2E9EE4, #833CE2)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
          transform: 'translate(-3px, 0px)'
        }
      },

      '&.active:hover': {
        padding: '8px',

        '& .MuiListItemText-root': {
          opacity: 1,
          position: 'absolute',
          left: '44px',
          background: 'linear-gradient(to right, #140430, #360A82)',
          padding: '10px 16px 10px 20px',
          borderRadius: '0 8px 8px 0',
          boxShadow: '4px 0 8px rgba(0,0,0,0.2)',
          minWidth: '120px',
          zIndex: 1200,
          visibility: 'visible',
          whiteSpace: 'nowrap'
        },

        '&::before': {
          content: '""',
          position: 'absolute',
          top: -1,
          left: -1,
          right: -1,
          bottom: -1,
          borderRadius: '8px',
          padding: '1px',
          background: 'linear-gradient(90deg, #2E9EE4, #833CE2)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          width: 'calc(100% + 138px)',
          pointerEvents: 'none',
          zIndex: 1201
        }
      },

      '&:hover': {
        background: 'transparent',

        '& .MuiListItemIcon-root': {
          transform: 'translateX(8px)'
        },

        '& .MuiListItemText-root': {
          opacity: 1,
          position: 'absolute',
          left: '48px',
          background: 'linear-gradient(to right, #140430, #360A82)',
          padding: '10px 16px 10px 20px',
          borderRadius: '0 8px 8px 0',
          boxShadow: '4px 0 8px rgba(0,0,0,0.2)',
          minWidth: '120px',
          zIndex: 1200,
          visibility: 'visible',
          whiteSpace: 'nowrap'
        }
      },

      '& .MuiListItemText-root': {
        opacity: 0,
        position: 'absolute',
        pointerEvents: 'none',
        visibility: 'hidden'
      }
    })
  }
}));

const SIDEBAR_MENU_OPTIONS: Array<{
  label: string;
  href: string;
  icon: iconMapKeys;
  subMenu?: Array<{ label: string; href: string; icon: iconMapKeys }>;
  separator?: boolean;
  disabled?: boolean;
}> = [
  {
    label: 'Settings',
    icon: 'setting',
    href: '/settings'
  },
  {
    label: 'Integrations',
    icon: 'integrations',
    href: '/integrations',
    separator: true,
    disabled: true
  },
  {
    label: 'Help Center',
    icon: 'integrations',
    href: '/integrations',
    disabled: true
  },
  {
    label: 'Developer Portal',
    icon: 'integrations',
    href: '/integrations',
    disabled: true
  },
  {
    label: 'Quick Support',
    icon: 'integrations',
    href: '/integrations',
    separator: true,
    disabled: true
  }
  // {
  //   label: 'Help',
  //   icon: 'help',
  //   href: '/help',
  //   subMenu: [
  //     {
  //       label: 'Help Center',
  //       icon: 'helpCenter',
  //       href: '/helpCenter'
  //     },
  //     {
  //       label: 'Developer Portal',
  //       icon: 'developerPortal',
  //       href: '/devPortal'
  //     },
  //     {
  //       label: 'Quick Support',
  //       icon: 'quickSupport',
  //       href: '/quickSupport'
  //     }
  //   ]
  // }
];

const UserInformation = ({ isOpen, handleLogout }: { isOpen: boolean; handleLogout: () => void }) => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);
  const userDetails = useSelector(makeSelectUserData());
  const handleClick = (event: React.MouseEvent<Element>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        m={isOpen ? 1.2 : 0}
        mb={isOpen ? 0 : 0.2}
        sx={{ background: 'linear-gradient(315deg, #360A82 0%, #140430 100%)', position: 'sticky', bottom: 0 }}
      >
        <Link underline="none">
          <StyledAccount open={isOpen}>
            {!isOpen ? (
              <Icon
                name="arrow-right-1-linear"
                styles={{ alignSelf: 'center' }}
                size="18"
                fill="none"
                color="#fff"
                onClick={handleClick}
              />
            ) : null}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Stack
                direction={isOpen ? 'row' : 'column'}
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                onClick={() => localRedirect('/settings/my-profile')}
                gap={2}
              >
                <Avatar
                  sx={{ width: isOpen ? 36 : 36, height: isOpen ? 36 : 36, borderRadius: '8px' }}
                  alt={userDetails?.profile?.name}
                />
                {isOpen && (
                  <Box>
                    {userDetails?.profile?.name ? (
                      <Typography
                        variant="body1"
                        textOverflow={'ellipsis'}
                        fontWeight={600}
                        overflow={'hidden'}
                        whiteSpace={'nowrap'}
                        title={userDetails?.profile?.name}
                        sx={{ color: 'white', width: '125px' }}
                      >
                        {userDetails?.profile?.name}
                      </Typography>
                    ) : (
                      <Skeleton width={100} variant="text" sx={{ fontSize: 16 }} />
                    )}
                    {userDetails?.profile?.status ? (
                      <Typography variant="body1" fontWeight={400} sx={{ color: 'white', opacity: '60%' }}>
                        {/* {userDetails?.profile?.status} */}Admin
                      </Typography>
                    ) : (
                      <Skeleton width={100} variant="text" sx={{ fontSize: '1rem' }} />
                    )}
                  </Box>
                )}
              </Stack>
              {isOpen ? (
                <Stack
                  direction="column"
                  alignItems="end"
                  justifyContent="center"
                  width="100%"
                  height="50px"
                  onClick={handleClick}
                >
                  <Icon name="arrow-right-1-linear" size="18" fill="none" color="#fff" />
                </Stack>
              ) : null}
            </div>
          </StyledAccount>
        </Link>
      </Box>
      <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
        {/* <Box sx={{ my: 1.5, px: 2 }}>
            <Stack direction="row" gap={1.5}>
              <Avatar />
              <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
                <Box>
                  {userDetails?.marketer?.name ? (
                    <Typography variant="subtitle1" noWrap>
                      {userDetails?.marketer?.name}
                    </Typography>
                  ) : (
                    <Skeleton width={100} variant="text" sx={{ fontSize: '1rem' }} />
                  )}

                  {userDetails?.profile?.businessEmail?.address ? (
                    <Typography
                      textOverflow="ellipsis"
                      variant="body1"
                      width={170}
                      sx={{ color: 'text.secondary' }}
                      noWrap
                    >
                      {userDetails?.profile?.businessEmail?.address}
                    </Typography>
                  ) : (
                    <Skeleton width={100} variant="text" sx={{ fontSize: 14 }} />
                  )}
                </Box>
                <Icon name="arrow-down-1-outline" size="16" fill="#292D32" />
              </Stack>
            </Stack>
          </Box>
          <Divider sx={{ mx: 2 }} /> */}
        <Stack sx={{ p: 1 }}>
          {SIDEBAR_MENU_OPTIONS.map(option => (
            <div key={option.label}>
              {option.subMenu ? (
                <NestedMenuItem label={option.label} leftIcon={option.icon} sx={{ px: 0.5, py: 0.5 }}>
                  {option.subMenu.map(item => (
                    <MenuItem key={item.label} onClick={handleClose}>
                      <Icon size="14" fill="none" name={item.icon} />
                      <Typography sx={{ color: '#000', paddingLeft: '8px' }}>{item.label}</Typography>
                    </MenuItem>
                  ))}
                </NestedMenuItem>
              ) : (
                <>
                  <IconMenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      localRedirect(option.href);
                    }}
                    leftIcon={<Icon size="14" fill="none" name={option.icon} styles={{ alignSelf: 'center' }} />}
                    label={option.label}
                    disabled={option.disabled}
                  />
                  {option.separator ? <Divider /> : null}
                </>
              )}
            </div>
          ))}
        </Stack>
        {/* <Divider sx={{ mx: 2 }} /> */}
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
    </>
  );
};

const Sidebar = ({ isOpen, handleToggleDrawer, handleLogout }: SidebarProps) => {
  // const { ref, height } = useElementSize<HTMLDivElement>();

  return (
    <Drawer variant="permanent" open={isOpen}>
      <Box height={height} className="left-sidebar-logo">
        <Toolbar
          sx={{
            px: [2],
            height: '100%'
          }}
        >
          <Stack
            direction={isOpen ? 'row' : 'column'}
            marginBottom={isOpen ? '15px' : '15px'}
            justifyContent="space-between"
            width="100%"
            alignItems="center"
            height="50px"
          >
            <Stack direction="row" alignItems="center" gap={2} my={1}>
              <img src={blinkLinkIcon} alt="BlinkLink" style={{ width: '32px', minWidth: '32px' }} />
              {isOpen && <img src={logoWhite} alt="BlinkLink" height={20} />}
            </Stack>
            <IconButton
              sx={{
                borderRadius: '8px',
                border: '1px solid rgba(182, 182, 182, 0.3)',
                width: '24px',
                height: '24px',
                padding: '0px'
              }}
              onClick={handleToggleDrawer}
              className={styles.iconButton}
            >
              <Icon
                name="double-arrow-left"
                size="16px"
                styles={{ ...(!isOpen && { transform: 'rotate(180deg)', transition: '0.3s all' }) }}
              />
            </IconButton>
          </Stack>
        </Toolbar>
      </Box>

      <Stack
        justifyContent="space-between"
        sx={{ height: `calc(100vh - ${height}px - 16px)`, overflow: isOpen ? 'auto' : 'none' }}
        className="left-sidebar-content"
      >
        <Box>
          <CollapsedList component="nav" open={isOpen}>
            {appRoutes.map((item, key) => (
              <ListItemButton component={NavLink} to={item.href} key={key} sx={{ mb: 1 }}>
                <ListItemIcon>
                  <Icon fill="white" styles={{ color: 'white', fill: 'white' }} name={item.icon} />
                </ListItemIcon>
                <ListItemText primary={<Typography variant="subtitle1">{item.label}</Typography>} />
              </ListItemButton>
            ))}
            <Divider sx={{ mx: 2, borderColor: 'white', opacity: 0.2 }} />

            {isOpen ? (
              <ListItemButton
                sx={{
                  margin: '4px 8px 0 8px !important',
                  marginBottom: 0,
                  paddingY: 0,
                  '&:hover': {
                    background: 'unset !important'
                  }
                }}
                // key={key}
                // onClick={() => localRedirect(item.href)}
                // selected={location.pathname.includes(item.href)}
                // style={{ marginLeft: location.pathname.includes(item.href) ? '0' : '5px' }}
              >
                {/* <ListItemIcon>
                  <Icon fill="white" styles={{ color: 'white', fill: 'white' }} name={item.icon} />
                </ListItemIcon> */}
                <ListItemText
                  primary={
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      Analytics
                    </Typography>
                  }
                />
              </ListItemButton>
            ) : null}

            {analyticsAppRoutes.map((item, key) => (
              <ListItemButton component={NavLink} to={item.href} key={key} sx={{ mb: 1 }}>
                <ListItemIcon>
                  <Icon fill="white" styles={{ color: 'white', fill: 'white' }} name={item.icon} />
                </ListItemIcon>
                <ListItemText primary={<Typography variant="subtitle1">{item.label}</Typography>} />
              </ListItemButton>
              // <ListItemButton
              //   sx={{ mb: 1, mt: key === 0 ? '0 !important' : 'inherit' }}
              //   key={key}
              //   onClick={() => localRedirect(item.href)}
              //   selected={location.pathname.includes(item.href)}
              //   style={{ marginLeft: location.pathname.includes(item.href) ? '0' : '5px' }}
              // >
              //   <ListItemIcon>
              //     <Icon fill="white" styles={{ color: 'white', fill: 'white' }} name={item.icon} />
              //   </ListItemIcon>
              //   <ListItemText primary={<Typography variant="subtitle1">{item.label}</Typography>} />
              // </ListItemButton>
            ))}
          </CollapsedList>
        </Box>

        <UserInformation isOpen={isOpen} handleLogout={handleLogout} />
      </Stack>
    </Drawer>
  );
};

export default Sidebar;
